import { t } from '@lingui/core/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { PetType } from '@rover/types/src/Pet';

import { DateData } from './types';

export enum HeaderScenariosEnum {
  SELECTED_SITTER = 'selected-sitter',
  BEST_MATCHES = 'best-matches',
  SEARCH_RESULTS = 'search-results',
  LAST_MINUTE_REQUEST = 'last-minute-request',
  LOW_SUPPLY = 'low-supply',
  NO_RESULTS = 'no-results',
}

export enum HeaderIconEnum {
  BOLT = 'NowBolt',
  EDIT = 'Edit',
  MAP = 'Map',
  PERSON = 'Person',
  SEARCH = 'Search',
  TIME = 'Time',
}

// TITLES
export enum HeaderTitlesEnum {
  AVAILABLE_SITTERS_NEAR_YOU = 'available-sitters-near-you',
  FIND_MATCH = 'find-a-match',
  OTHER_SITTERS_IN_AREA = 'other-sitters-in-your-area',
  OTHER_AVAILABLE_SITTERS_IN_AREA = 'other-available-sitters-in-your-area',
  SITTERS_CLOSE_TO_YOU = 'sitters-close-to-you',
  SITTERS_IN_AREA = 'sitters-in-your-area',
  SELECTED_SITTER_TITLE = 'your-selected-sitter',
  SELECTED_SITTER_UNAVAILABLE_TITLE = 'your-selected-sitter-is-not-available',
  BEST_MATCHES = 'best-matches-for-your-request',
  LAST_MINUTE_SAME_DAY = 'last-minute-same-day',
  LAST_MINUTE_NEXT_DAY = 'last-minute-next-day',
  LAST_MINUTE_TWO_DAY = 'last-minute-two-day',
}

// Last-minute titles are resolved dynamically via buildLastMinuteRequestTitle,
// so they are excluded from the static title map below.
export type LastMinuteHeaderTitle =
  | HeaderTitlesEnum.LAST_MINUTE_SAME_DAY
  | HeaderTitlesEnum.LAST_MINUTE_NEXT_DAY
  | HeaderTitlesEnum.LAST_MINUTE_TWO_DAY;

export const HEADER_TITLES = (): Record<
  Exclude<HeaderTitlesEnum, LastMinuteHeaderTitle>,
  string
> => ({
  [HeaderTitlesEnum.AVAILABLE_SITTERS_NEAR_YOU]: t`Available sitters near you`,
  [HeaderTitlesEnum.FIND_MATCH]: t`Find a match`,
  [HeaderTitlesEnum.OTHER_SITTERS_IN_AREA]: t`Other sitters in your area`,
  [HeaderTitlesEnum.OTHER_AVAILABLE_SITTERS_IN_AREA]: `Other available sitters in your area`,
  [HeaderTitlesEnum.SITTERS_CLOSE_TO_YOU]: t`Sitters close to you`,
  [HeaderTitlesEnum.SITTERS_IN_AREA]: t`Sitters in your area`,
  [HeaderTitlesEnum.SELECTED_SITTER_TITLE]: t`Your selected sitter`,
  [HeaderTitlesEnum.SELECTED_SITTER_UNAVAILABLE_TITLE]: t`Your selected sitter is not available`,
  [HeaderTitlesEnum.BEST_MATCHES]: t`Best matches for your request`,
});

// SUBHEADERS
export enum SubHeadersEnum {
  ADD_DATES,
  SHOWING_OTHER_PROVIDERS,
  SEEING_PROVIDERS_DATES,
  BEST_MATCH_SELECTED_PROVIDER_SHORT_LEAD_TIME,
  BEST_MATCH_LIST_SHORT_LEAD_TIME,
  SELECTED_SITTER_NO_DATES,
  SELECTED_SITTER_AVAILABLE,
  SELECTED_SITTER_UNAVAILABLE,
  POST_SELECTED_SITTER_RESULTS,
}

const getAddDatesSubHeader = (serviceType?: ServiceType, petCount?: number): string => {
  if (!serviceType) {
    return '';
  }
  if (petCount && petCount > 0) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`Add dates to see boarding sitters who'll be available.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Add dates to see daycare sitters who'll be available.`;
      case ServiceType.DROP_IN:
        return t`Add dates to see drop-in sitters who'll be available.`;
      case ServiceType.DOG_WALKING:
        return t`Add dates to see dog walkers who'll be available.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`Add dates to see sitters who'll be available.`;
    }
  } else {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`Add dates and pets to see boarding sitters who'll be available.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Add dates and pets to see daycare sitters who'll be available.`;
      case ServiceType.DROP_IN:
        return t`Add dates and pets to see drop-in sitters who'll be available.`;
      case ServiceType.DOG_WALKING:
        return t`Add dates and pets to see dog walkers who'll be available.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`Add dates and pets to see sitters who'll be available.`;
    }
  }
};

const getShowingOtherProvidersSubHeader = (serviceType?: ServiceType): string => {
  if (!serviceType) {
    return '';
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
      return t`Showing other available boarding sitters.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Showing other available daycare sitters.`;
    case ServiceType.DROP_IN:
      return t`Showing other available drop-in sitters.`;
    case ServiceType.DOG_WALKING:
      return t`Showing other available dog walkers.`;
    case ServiceType.OVERNIGHT_TRAVELING:
    default:
      return t`Showing other available sitters.`;
  }
};

const getYoureSeeingProviders = (serviceType?: ServiceType, dateData?: DateData): string => {
  if (!serviceType || !dateData) {
    return '';
  }

  const { isSameDate } = dateData || {};
  const startDate = dateData?.startDate ?? '';
  const endDate = dateData?.endDate ?? '';
  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`Showing boarding sitters available from **${startDate}**. Add your pets to get accurate pricing.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Showing daycare sitters available from **${startDate}**. Add your pets to get accurate pricing.`;
      case ServiceType.DROP_IN:
        return t`Showing drop-in sitters available from **${startDate}**. Add your pets to get accurate pricing.`;
      case ServiceType.DOG_WALKING:
        return t`Showing dog walkers available from **${startDate}**. Add your pets to get accurate pricing.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`Showing sitters available from **${startDate}**. Add your pets to get accurate pricing.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
      return t`Showing boarding sitters available from **${startDate} to ${endDate}**. Add your pets to get accurate pricing.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Showing daycare sitters available from **${startDate} to ${endDate}**. Add your pets to get accurate pricing.`;
    case ServiceType.DROP_IN:
      return t`Showing drop-in sitters available from **${startDate} to ${endDate}**. Add your pets to get accurate pricing.`;
    case ServiceType.DOG_WALKING:
      return t`Showing dog walkers available from **${startDate} to ${endDate}**. Add your pets to get accurate pricing.`;
    case ServiceType.OVERNIGHT_TRAVELING:
    default:
      return t`Showing sitters available from **${startDate} to ${endDate}**. Add your pets to get accurate pricing.`;
  }
};

const getSelectedSitterAvailable = (sitterName?: string, dateData?: DateData): string => {
  if (!sitterName || !dateData) {
    return '';
  }

  const { isSameDate } = dateData || {};
  const startDate = dateData?.startDate ?? '';
  const endDate = dateData?.endDate ?? '';

  if (isSameDate) {
    return t`${sitterName} is available on **${startDate}**.`;
  }

  return t`${sitterName} is available from **${startDate} to ${endDate}**.`;
};

const getSelectedSitterUnavailable = (sitterName?: string, dateData?: DateData): string => {
  if (!sitterName || !dateData) {
    return '';
  }

  const { isSameDate } = dateData || {};
  const startDate = dateData?.startDate ?? '';
  const endDate = dateData?.endDate ?? '';

  if (isSameDate) {
    return t`${sitterName} is not available on **${startDate}**.`;
  }

  return t`${sitterName} is not available from **${startDate} to ${endDate}**.`;
};

const getPostSelectedSitterResults = (serviceType?: ServiceType, dateData?: DateData): string => {
  if (!serviceType || !dateData) {
    return '';
  }

  const { isSameDate } = dateData || {};
  const startDate = dateData?.startDate ?? '';
  const endDate = dateData?.endDate ?? '';

  if (isSameDate) {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
        return t`Showing boarding sitters available on **${startDate}**.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Showing daycare sitters available on **${startDate}**.`;
      case ServiceType.DROP_IN:
        return t`Showing drop-in sitters available on **${startDate}**.`;
      case ServiceType.DOG_WALKING:
        return t`Showing dog walkers available on **${startDate}**.`;
      case ServiceType.OVERNIGHT_TRAVELING:
      default:
        return t`Showing sitters available on **${startDate}**.`;
    }
  }

  switch (serviceType) {
    case ServiceType.OVERNIGHT_BOARDING:
      return t`Showing boarding sitters available from **${startDate} to ${endDate}**.`;
    case ServiceType.DOGGY_DAY_CARE:
      return t`Showing daycare sitters available from **${startDate} to ${endDate}**.`;
    case ServiceType.DROP_IN:
      return t`Showing drop-in sitters available from **${startDate} to ${endDate}**.`;
    case ServiceType.DOG_WALKING:
      return t`Showing dog walkers available from **${startDate} to ${endDate}**.`;
    case ServiceType.OVERNIGHT_TRAVELING:
    default:
      return t`Showing sitters available from **${startDate} to ${endDate}**.`;
  }
};

export const getSubHeader = (
  subHeader: SubHeadersEnum,
  params: {
    serviceType?: ServiceType;
    sitterName?: string;
    dateData?: DateData;
    petCount?: number;
  } = {}
): string => {
  const { serviceType, sitterName, dateData, petCount } = params;
  switch (subHeader) {
    case SubHeadersEnum.ADD_DATES:
      return getAddDatesSubHeader(serviceType, petCount);
    case SubHeadersEnum.SHOWING_OTHER_PROVIDERS:
      return getShowingOtherProvidersSubHeader(serviceType);
    case SubHeadersEnum.SEEING_PROVIDERS_DATES:
      return getYoureSeeingProviders(serviceType, dateData);
    case SubHeadersEnum.SELECTED_SITTER_NO_DATES:
      if (serviceType === ServiceType.DOG_WALKING) {
        return t`Add dates to see if this dog walker is available.`;
      }
      return t`Add dates to see if this sitter is available.`;
    case SubHeadersEnum.SELECTED_SITTER_AVAILABLE:
      return getSelectedSitterAvailable(sitterName, dateData);
    case SubHeadersEnum.SELECTED_SITTER_UNAVAILABLE:
      return getSelectedSitterUnavailable(sitterName, dateData);
    case SubHeadersEnum.POST_SELECTED_SITTER_RESULTS:
      return getPostSelectedSitterResults(serviceType, dateData);
    case SubHeadersEnum.BEST_MATCH_SELECTED_PROVIDER_SHORT_LEAD_TIME:
      if (serviceType === 'dog-walking') {
        return t`This dog walker accepts last-minute requests.`;
      }
      return t`This sitter accepts last-minute requests.`;
    case SubHeadersEnum.BEST_MATCH_LIST_SHORT_LEAD_TIME:
      if (serviceType === 'dog-walking') {
        return t`These dog walkers accept last-minute requests.`;
      }
      return t`These sitters accept last-minute requests.`;
    default:
      return '';
  }
};

// ALERTS - Price Transparency
export enum AlertContentEnum {
  PRICE_TRANSPARENCY_FULL_ADD_DATES,
  PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT,
  PRICE_TRANSPARENCY_FULL_SEO_PAGE,
  PRICE_TRANSPARENCY_LITE_SEO_PAGE,
  PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_AVAILABLE,
  PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_UNAVAILABLE,
  PRICE_TRANSPARENCY_FULL_SITTER_PROFILE,
  PRICE_TRANSPARENCY_FULL_CONTACT_MORE_SITTERS,
  PRICE_TRANSPARENCY_LITE_CONTACT_MORE_SITTERS,
}

type AlertContentParams = {
  dateData?: DateData;
  bookingFeePercentage?: string;
  individualPetTypeCountDescription?: string;
  serviceType: ServiceType | undefined;
  sitterName?: string;
  petType?: PetType;
};

export class PriceTransparencyStringCollection {
  static generate(
    methods: Array<(params: AlertContentParams) => string>,
    params: AlertContentParams
  ): string {
    return methods.map((method) => method.bind(this, params)()).join(' ');
  }

  static addDates(): string {
    return t`Add your dates to get accurate pricing.`;
  }

  static addPets(): string {
    return t`Add your pets to get accurate pricing.`;
  }

  static addDatesAndPets(): string {
    return t`Add your dates and pets to get accurate pricing.`;
  }

  static ptFullBookingFeeNotice({ bookingFeePercentage = '' }: AlertContentParams): string {
    return t`Pricing may vary based on your search and includes a <0>booking fee</0> of ${bookingFeePercentage}.`;
  }

  static ptLiteBookingFeeNotice({ bookingFeePercentage = '' }: AlertContentParams): string {
    return t`Pricing may vary based on your search. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }

  static sitterAvailable({ sitterName = '' }: AlertContentParams): string {
    return t`${sitterName} is available.`;
  }

  static sitterNotAvailable({ sitterName = '' }: AlertContentParams): string {
    return t`${sitterName} is not available.`;
  }

  static getPetTypeDescriptionForPetType(petType?: PetType): string {
    switch (petType) {
      case PetType.CAT:
        return t`1 cat`;
      case PetType.PUPPY:
        return t`1 puppy`;
      case PetType.DOG:
      default:
        return t`1 dog`;
    }
  }

  private static pricesDescriptionSameDatesPetType(
    serviceType: ServiceType | undefined,
    startDate: string,
    petTypeDescription: string
  ): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per night for ${petTypeDescription} on ${startDate}.`;
      case ServiceType.DOGGY_DAY_CARE:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per day for ${petTypeDescription} on ${startDate}.`;
      case ServiceType.DOG_WALKING:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per walk for ${petTypeDescription} on ${startDate}.`;
      case ServiceType.DROP_IN:
      default:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per visit for ${petTypeDescription} on ${startDate}.`;
    }
  }

  private static pricesDescriptionDistinctDatesPetType(
    serviceType: ServiceType | undefined,
    startDate: string,
    endDate: string,
    petTypeDescription: string
  ): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per night for ${petTypeDescription} from ${startDate} to ${endDate}.`;
      case ServiceType.DOGGY_DAY_CARE:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per day for ${petTypeDescription} from ${startDate} to ${endDate}.`;
      case ServiceType.DOG_WALKING:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per walk for ${petTypeDescription} from ${startDate} to ${endDate}.`;
      case ServiceType.DROP_IN:
      default:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are per visit for ${petTypeDescription} from ${startDate} to ${endDate}.`;
    }
  }

  private static pricesDescriptionNoDatesPetType(
    serviceType: ServiceType | undefined,
    petTypeDescription: string
  ): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are estimates per night for ${petTypeDescription}.`;
      case ServiceType.DOGGY_DAY_CARE:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are estimates per day for ${petTypeDescription}.`;
      case ServiceType.DOG_TRAINING:
        // TODO (DEV-104195): Translate this before going international
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return `Prices are estimates per training session for ${petTypeDescription}.`;
      case ServiceType.DOG_WALKING:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are estimates per walk for ${petTypeDescription}.`;
      case ServiceType.DROP_IN:
      default:
        /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
        return t`Prices are estimates per visit for ${petTypeDescription}.`;
    }
  }

  private static pricesDescriptionSameDatesNoPetType(
    serviceType: ServiceType | undefined,
    startDate: string
  ): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`Prices are per night on ${startDate}.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Prices are per day on ${startDate}.`;
      case ServiceType.DOG_WALKING:
        return t`Prices are per walk on ${startDate}.`;
      case ServiceType.DROP_IN:
      default:
        return t`Prices are per visit on ${startDate}.`;
    }
  }

  private static pricesDescriptionDistinctDatesNoPetType(
    serviceType: ServiceType | undefined,
    startDate: string,
    endDate: string
  ): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`Prices are per night from ${startDate} to ${endDate}.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Prices are per day from ${startDate} to ${endDate}.`;
      case ServiceType.DOG_WALKING:
        return t`Prices are per walk from ${startDate} to ${endDate}.`;
      case ServiceType.DROP_IN:
      default:
        return t`Prices are per visit from ${startDate} to ${endDate}.`;
    }
  }

  private static pricesDescriptionNoDatesNoPetType(serviceType: ServiceType | undefined): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`Prices are estimates per night.`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`Prices are estimates per day.`;
      case ServiceType.DOG_WALKING:
        return t`Prices are estimates per walk.`;
      case ServiceType.DROP_IN:
      default:
        return t`Prices are estimates per visit.`;
    }
  }

  private static pricesPerUnitDescription(serviceType: ServiceType | undefined): string {
    switch (serviceType) {
      case ServiceType.OVERNIGHT_BOARDING:
      case ServiceType.OVERNIGHT_TRAVELING:
        return t`per night`;
      case ServiceType.DOGGY_DAY_CARE:
        return t`per day`;
      case ServiceType.DOG_WALKING:
        return t`per walk`;
      case ServiceType.DROP_IN:
      default:
        return t`per visit`;
    }
  }

  static pricesDescription({
    serviceType,
    dateData,
    individualPetTypeCountDescription,
    petType,
  }: AlertContentParams): string {
    let petTypeDescription = individualPetTypeCountDescription;
    if (petType) {
      petTypeDescription = this.getPetTypeDescriptionForPetType(petType);
    }

    const hasDates = dateData && !!dateData.startDate && !!dateData.endDate;

    if (!hasDates) {
      if (petTypeDescription) {
        return this.pricesDescriptionNoDatesPetType(serviceType, petTypeDescription);
      }
      return this.pricesDescriptionNoDatesNoPetType(serviceType);
    }

    const { isSameDate } = dateData;
    const startDate = dateData.startDate || '';
    const endDate = dateData.endDate || '';
    if (petTypeDescription) {
      if (isSameDate) {
        return this.pricesDescriptionSameDatesPetType(serviceType, startDate, petTypeDescription);
      }
      return this.pricesDescriptionDistinctDatesPetType(
        serviceType,
        startDate,
        endDate,
        petTypeDescription
      );
    }

    if (isSameDate) {
      return this.pricesDescriptionSameDatesNoPetType(serviceType, startDate);
    }
    return this.pricesDescriptionDistinctDatesNoPetType(serviceType, startDate, endDate);
  }

  static ptFullSitterProfile({ bookingFeePercentage = '', petType }: AlertContentParams): string {
    const petTypeDescription = this.getPetTypeDescriptionForPetType(petType);
    /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
    return t`These prices are for ${petTypeDescription}, including a <0>booking fee</0> of ${bookingFeePercentage}.`;
  }

  static ptCondensedSitterProfilePetType({ petType }: Pick<AlertContentParams, 'petType'>): string {
    const petTypeDescription = this.getPetTypeDescriptionForPetType(petType);
    /* i18n: {petTypeDescription} may refer to any combination of pet counts for a service. Examples include: "3 dogs" or "1 dog and 2 puppies" or "2 dogs, 1 puppy, and 2 cats" */
    return t`These prices are for ${petTypeDescription}.`;
  }

  static ptFullContactMoreSitters({
    bookingFeePercentage = '',
    serviceType,
  }: AlertContentParams): string {
    const pricesPerUnitDescription = this.pricesPerUnitDescription(serviceType);
    /* i18n: {pricesPerUnitDescription} refers to the unit of pricing for a service. Examples include: "per night" or "per day" or "per visit" */
    return t`You’re seeing prices ${pricesPerUnitDescription}, including a <0>booking fee</0> of ${bookingFeePercentage}.`;
  }

  static ptLiteContactMoreSitters({
    bookingFeePercentage = '',
    serviceType,
  }: AlertContentParams): string {
    const pricesPerUnitDescription = this.pricesPerUnitDescription(serviceType);
    /* i18n: {pricesPerUnitDescription} refers to the unit of pricing for a service. Examples include: "per night" or "per day" or "per visit" */
    return t`You’re seeing prices ${pricesPerUnitDescription}. A <0>booking fee</0> of ${bookingFeePercentage} and taxes will apply.`;
  }
}

export const getAlertContent = (
  alertContent: AlertContentEnum,
  params: AlertContentParams
): string => {
  // Make sure the AlertContentEnum takes precedence in the logic of pricesDescription instead
  // of the passed params, so we remove params that shouldn't be used in certain AlertContentEnum:
  // e.g. for a SEO page we remove the dates, even if passed, they are ignored.
  const ptFullParams = params;
  const ptFullSeoParams = {
    ...params,
    dateData: undefined,
  };
  const ptLiteSeoParams = {
    ...ptFullSeoParams,
    individualPetTypeCountDescription: undefined,
    petType: undefined,
  };

  switch (alertContent) {
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_ADD_DATES:
      return PriceTransparencyStringCollection.generate(
        [
          PriceTransparencyStringCollection.addDates,
          PriceTransparencyStringCollection.pricesDescription,
          PriceTransparencyStringCollection.ptFullBookingFeeNotice,
        ],
        ptFullParams
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_PRICES_FOR_UNIT:
      return PriceTransparencyStringCollection.generate(
        [
          PriceTransparencyStringCollection.pricesDescription,
          PriceTransparencyStringCollection.ptFullBookingFeeNotice,
        ],
        ptFullParams
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SEO_PAGE:
      return PriceTransparencyStringCollection.generate(
        [
          PriceTransparencyStringCollection.pricesDescription,
          PriceTransparencyStringCollection.ptFullBookingFeeNotice,
        ],
        ptFullSeoParams
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_SEO_PAGE:
      return PriceTransparencyStringCollection.generate(
        [
          PriceTransparencyStringCollection.pricesDescription,
          PriceTransparencyStringCollection.ptLiteBookingFeeNotice,
        ],
        ptLiteSeoParams
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_AVAILABLE:
      return PriceTransparencyStringCollection.generate(
        [
          PriceTransparencyStringCollection.sitterAvailable,
          PriceTransparencyStringCollection.pricesDescription,
          PriceTransparencyStringCollection.ptFullBookingFeeNotice,
        ],
        ptFullParams
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SELECTED_SITTER_UNAVAILABLE:
      return PriceTransparencyStringCollection.generate(
        [
          PriceTransparencyStringCollection.sitterNotAvailable,
          PriceTransparencyStringCollection.pricesDescription,
          PriceTransparencyStringCollection.ptFullBookingFeeNotice,
        ],
        ptFullParams
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_SITTER_PROFILE:
      return PriceTransparencyStringCollection.generate(
        [PriceTransparencyStringCollection.ptFullSitterProfile],
        params
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_FULL_CONTACT_MORE_SITTERS:
      return PriceTransparencyStringCollection.generate(
        [PriceTransparencyStringCollection.ptFullContactMoreSitters],
        params
      );
    case AlertContentEnum.PRICE_TRANSPARENCY_LITE_CONTACT_MORE_SITTERS:
      return PriceTransparencyStringCollection.generate(
        [PriceTransparencyStringCollection.ptLiteContactMoreSitters],
        params
      );
    default:
      return '';
  }
};
